import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import RecipeForm, { type RecipeFormValues } from "../components/RecipeForm";
import { useCreateRecipe } from "../hooks/useCreateRecipe";
import { useAuth } from "../context/AuthContext";
import { uploadImage } from "../lib/uploadImage";
import { importRecipeFromUrl } from "../lib/api";

export default function AddRecipePage() {
  const { mutateAsync } = useCreateRecipe();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [urlInput, setUrlInput] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importKey, setImportKey] = useState(0);
  const [importedValues, setImportedValues] = useState<Partial<RecipeFormValues> | undefined>(
    undefined
  );

  async function handleImport(e: FormEvent) {
    e.preventDefault();
    if (!urlInput.trim()) return;
    setImportError(null);
    setImporting(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      const data = await importRecipeFromUrl(urlInput.trim(), token);
      setImportedValues(data as Partial<RecipeFormValues>);
      setImportKey((k) => k + 1);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  async function handleSubmit(values: RecipeFormValues, imageFile: File | null) {
    const token = await getToken();
    if (!token) throw new Error("Not authenticated");

    let imageKey = values.imageKey;
    let thumbnailKey = values.thumbnailKey;

    if (imageFile) {
      const uploaded = await uploadImage(imageFile, token);
      imageKey = uploaded.imageKey;
      thumbnailKey = uploaded.thumbnailKey;
    }

    const recipe = await mutateAsync({ ...values, imageKey, thumbnailKey });
    navigate(`/recipe/${recipe.slug}`);
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-2">
        <h1
          className="text-2xl font-semibold text-gray-900 mb-6"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Add Recipe
        </h1>

        {/* URL import */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">
            Import from a URL{" "}
            <span className="font-normal text-gray-400">
              (AllRecipes, Serious Eats, NYT Cooking, etc.)
            </span>
          </p>
          <form onSubmit={handleImport} className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://www.allrecipes.com/recipe/..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
            />
            <button
              type="submit"
              disabled={importing || !urlInput.trim()}
              className="px-4 py-2 bg-herb text-white text-sm font-medium rounded-lg hover:bg-herb-light disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {importing ? "Importing…" : "Import"}
            </button>
          </form>
          {importError && (
            <p className="mt-2 text-sm text-red-600">{importError}</p>
          )}
          {importedValues && !importError && (
            <p className="mt-2 text-sm text-green-600">
              ✓ Recipe imported — review and edit below, then save.
            </p>
          )}
        </div>
      </div>

      <RecipeForm
        key={importKey}
        initialValues={importedValues}
        onSubmit={handleSubmit}
        submitLabel="Create Recipe"
      />
    </div>
  );
}
